import { useState, useCallback, useRef, useMemo } from 'react';

/**
 * Drag and drop state data
 */
export interface DragDropData {
  isDragging: boolean;
  isDragOver: boolean;
}

/**
 * Hook for handling drag and drop functionality
 */
/**
 * The slice of a drag event these handlers touch. Stated structurally so a
 * caller — or a test — supplies only what is read, rather than a whole
 * synthetic React event.
 */
export type DragEventLike = {
  preventDefault: () => void;
  stopPropagation: () => void;
  dataTransfer?: {
    items?: ArrayLike<unknown> | null;
    files?: ArrayLike<File> | null;
    dropEffect?: string;
  } | null;
};

/** The slice of a file-input change event `handleFileInputChange` touches. */
export type FileInputChangeEventLike = {
  target: { files?: ArrayLike<File> | null; value: string };
};

export function useDragDrop(onFilesDropped: (files: File[]) => void, disabled = false, acceptedMimeTypes?: string[]) {
  const [dragData, setDragData] = useState<DragDropData>({
    isDragging: false,
    isDragOver: false,
  });

  const dragCounter = useRef(0);

  /**
   * Handles drag enter events
   */
  const handleDragEnter = useCallback(
    (event: DragEventLike) => {
      event.preventDefault();
      event.stopPropagation();

      if (disabled) return;

      dragCounter.current++;

      if (event.dataTransfer?.items != null && event.dataTransfer.items.length > 0) {
        setDragData({
          isDragging: true,
          isDragOver: true,
        });
      }
    },
    [disabled],
  );

  /**
   * Handles drag leave events
   */
  const handleDragLeave = useCallback(
    (event: DragEventLike) => {
      event.preventDefault();
      event.stopPropagation();

      if (disabled) return;

      dragCounter.current--;

      if (dragCounter.current === 0) {
        setDragData({
          isDragging: false,
          isDragOver: false,
        });
      }
    },
    [disabled],
  );

  /**
   * Handles drag over events
   */
  const handleDragOver = useCallback(
    (event: DragEventLike) => {
      event.preventDefault();
      event.stopPropagation();

      if (disabled) return;

      // Set the drag effect to copy to show the user they can drop here
      if (event.dataTransfer != null) {
        event.dataTransfer.dropEffect = 'copy';
      }
    },
    [disabled],
  );

  /**
   * Handles drop events
   */
  const handleDrop = useCallback(
    (event: DragEventLike) => {
      event.preventDefault();
      event.stopPropagation();

      if (disabled) return;

      // Reset drag state
      dragCounter.current = 0;
      setDragData({
        isDragging: false,
        isDragOver: false,
      });

      if (event.dataTransfer?.files != null && event.dataTransfer.files.length > 0) {
        const files = Array.from(event.dataTransfer.files);
        onFilesDropped(files);
      }
    },
    [disabled, onFilesDropped],
  );

  /**
   * Handles file input change events
   */
  const handleFileInputChange = useCallback(
    (event: FileInputChangeEventLike) => {
      if (disabled) return;

      if (event.target.files != null && event.target.files.length > 0) {
        const files = Array.from(event.target.files);
        onFilesDropped(files);

        // Clear the input so the same file can be selected again
        event.target.value = '';
      }
    },
    [disabled, onFilesDropped],
  );

  /**
   * Creates drag and drop event handlers for a container element
   */
  const dragDropProps = useMemo(
    () => ({
      onDragEnter: handleDragEnter,
      onDragLeave: handleDragLeave,
      onDragOver: handleDragOver,
      onDrop: handleDrop,
    }),
    [handleDragEnter, handleDragLeave, handleDragOver, handleDrop],
  );

  /**
   * Creates props for a file input element
   */
  const fileInputProps = useMemo(
    () => ({
      type: 'file' as const,
      multiple: true,
      accept: acceptedMimeTypes?.join(',') ?? '*/*',
      onChange: handleFileInputChange,
      style: { display: 'none' } as const,
    }),
    [handleFileInputChange, acceptedMimeTypes],
  );

  return {
    dragData,
    dragDropProps,
    fileInputProps,
  };
}
